/**
 * @lodex-pro/web-admin/features/map-usage/hooks — 86baefu3r.
 *
 * Reads GET /v1/platform/map-usage (PLATFORM_ADMIN only — the table behind it has no RLS,
 * so that route guard is the whole of the isolation; see migration 0099).
 *
 * The period is a `YYYY-MM` string chosen by the caller, so it is part of the query key —
 * otherwise switching months would serve the previous month's cached rows under the new
 * label, which is the worst kind of wrong: plausible and silent.
 */
import { useApiQuery, useApiOpts } from '@lodex-pro/web-shared';

export interface MapUsageTenant {
  readonly tenantId: string;
  readonly name: string;
  readonly haulerAbbreviation: string | null;
  readonly isActive: boolean;
  readonly loadCount: number;
  /**
   * false = no counter row exists for this tenant in this month. Deliberately distinct
   * from `loadCount === 0` on an existing row: "never reported" and "reported nothing" are
   * different facts and the UI must not merge them.
   */
  readonly hasData: boolean;
  readonly firstLoadedAt: string | null;
  readonly lastLoadedAt: string | null;
}

export interface MapUsageResponse {
  /** `YYYY-MM`, echoed back from what the server actually resolved. */
  readonly month: string;
  /** Prebuilt server-side (e.g. "October 2026") so every surface labels it identically. */
  readonly monthLabel: string;
  readonly totalLoads: number;
  readonly reportingTenants: number;
  readonly tenantCount: number;
  readonly tenants: readonly MapUsageTenant[];
}

export const MAP_USAGE_KEY = ['platform', 'map-usage'] as const;

export function useMapUsage(
  month: string | null,
): ReturnType<typeof useApiQuery<MapUsageResponse>> {
  const opts = useApiOpts();
  const qs = month === null ? '' : `?month=${encodeURIComponent(month)}`;
  return useApiQuery<MapUsageResponse>(opts, `/v1/platform/map-usage${qs}`, {
    // month in the key — see the header.
    queryKey: [...MAP_USAGE_KEY, month ?? 'current'],
    staleTime: 30_000,
  });
}

/** Last `count` months as `YYYY-MM`, newest first, in UTC to match the server's buckets. */
export function recentMonths(count: number, now: Date = new Date()): readonly string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i += 1) {
    // Date.UTC normalises a negative month index into the previous year, so this needs no
    // year/month arithmetic of its own.
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(`${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}
